import React, { useMemo } from 'react';

/**
 * TotalsRibbon — single-line money summary inside the disc footer.
 *
 *   Today: ₹2,000   ·   Pending: ₹1,200
 *
 * Lets the doctor (and anyone reading over their shoulder) see
 * collectable totals without doing mental math. Receptionist asks
 * "how much do I collect?" — the answer is right there.
 *
 * Logic:
 *   - "Today" is the sum of every procedure on this visit, minus
 *     discounts. This is the patient's headline price.
 *   - "Pending" is what hasn't been collected yet. We approximate as
 *     the sum across sittings whose status is one of awaiting_payment
 *     / payment_cleared / scheduled / in_progress / pending_approval
 *     / completed (i.e. anything pre-paid). Cancelled and paid roll
 *     out of pending.
 *
 * Props:
 *   sittings — current Sitting[] for this visit
 *   palette  — disc theme tokens
 */

const PENDING_STATUSES = new Set([
  'awaiting_payment',
  'payment_cleared',
  'scheduled',
  'in_progress',
  'pending_approval',
  'completed',          // pay-after sittings not yet paid show as completed→awaiting_payment
  'needs_reassignment',
]);

function chipCost(sitting) {
  const raw =
    sitting?.final_cost
    ?? sitting?.estimated_cost
    ?? sitting?.effective_cost_snapshot
    ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function formatRupees(n) {
  if (!Number.isFinite(n) || n <= 0) return '₹0';
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

export default function TotalsRibbon({ sittings, palette }) {
  const totals = useMemo(() => {
    let today = 0;
    let pending = 0;
    for (const s of (sittings || [])) {
      const cost = chipCost(s);
      const status = s?.status;
      if (status === 'cancelled') continue;
      today += cost;
      if (status !== 'paid' && PENDING_STATUSES.has(status)) {
        pending += cost;
      }
    }
    return { today, pending };
  }, [sittings]);

  if (!sittings || sittings.length === 0) return null;

  const pendingColor = totals.pending > 0 ? '#b45309' : palette.muted;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 12,
        color: palette.text,
      }}
    >
      <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ fontSize: '0.5625rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.6 }}>
          Today
        </span>
        <strong style={{ fontSize: '0.9375rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>
          {formatRupees(totals.today)}
        </strong>
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 1, textAlign: 'right' }}>
        <span style={{ fontSize: '0.5625rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.7, color: pendingColor }}>
          Pending
        </span>
        <strong style={{ fontSize: '0.9375rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em', color: pendingColor }}>
          {formatRupees(totals.pending)}
        </strong>
      </span>
    </div>
  );
}
